import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Stack } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import ExpensesSection from '@/features/investments/form/components/ExpensesSection.tsx';
import FinancingSection from '@/features/investments/form/components/FinancingSection.tsx';
import IdentitySection from '@/features/investments/form/components/IdentitySection.tsx';
import IncomeSection from '@/features/investments/form/components/IncomeSection.tsx';
import MediaSection from '@/features/investments/form/components/MediaSection.tsx';
import PurchaseSection from '@/features/investments/form/components/PurchaseSection.tsx';
import StateSection from '@/features/investments/form/components/StateSection.tsx';
import {
  getEmptyInvestmentFormData,
  investmentFormSchema,
  InvestmentFormData,
} from '@/features/investments/form/investment-form.schema.ts';

export interface InvestmentFormProps {
  initialData?: InvestmentFormData;
  onSubmit: (data: InvestmentFormData) => Promise<void>;
  onCancel: () => void;
  submitLabel: string;
  loadingLabel: string;
  isLoading: boolean;
  error: string | null;
  hasRentalHistory?: boolean;
  onErrorDismiss: () => void;
}

const InvestmentForm = ({
  initialData,
  onSubmit,
  onCancel,
  submitLabel,
  loadingLabel,
  isLoading,
  error,
  hasRentalHistory = false,
  onErrorDismiss,
}: InvestmentFormProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const form = useForm<InvestmentFormData>({
    resolver: zodResolver(investmentFormSchema),
    defaultValues: initialData ?? getEmptyInvestmentFormData(),
  });

  const scrollToFirstError = useCallback((errors: FieldErrors<InvestmentFormData>) => {
    const firstErrorKey = Object.keys(errors)[0];
    if (!firstErrorKey || !formRef.current) return;

    const errorElement =
      formRef.current.querySelector(`[name="${firstErrorKey}"]`) ??
      formRef.current.querySelector(`[aria-invalid="true"]`);

    if (errorElement) {
      errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  useEffect(() => {
    form.reset(initialData ?? getEmptyInvestmentFormData());
  }, [form, initialData]);

  return (
    <FormProvider {...form}>
      <form ref={formRef} onSubmit={form.handleSubmit(onSubmit, scrollToFirstError)}>
        <Stack spacing={3}>
          {error ? (
            <Alert severity="error" onClose={onErrorDismiss}>
              {error}
            </Alert>
          ) : null}

          <IdentitySection />
          <StateSection />
          <PurchaseSection />
          <IncomeSection hasRentalHistory={hasRentalHistory} />
          <ExpensesSection />
          <FinancingSection />
          <MediaSection isLoading={isLoading} />

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-end',
              pt: 1,
            }}
          >
            <Button onClick={onCancel} disabled={isLoading} variant="outlined">
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading} sx={{ minWidth: 140 }}>
              {isLoading ? loadingLabel : submitLabel}
            </Button>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
};

export default InvestmentForm;
